import { Box, Grid } from '@mui/material';
import { useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { IJob, IJobCandidateInteraction } from 'wisestep-api';
import { WsListingLoader } from 'wisestep-base';

import useTalentPoolSearch from '../../../../features/candidates/talentpool/hooks/useTalentPoolSearch';
import { FiltersTagProp } from '../../components/filterTagList/FiltersTagList';
import {
  getActiveFilters,
  getFormDefaultFilters,
  getJobTalentPoolDefaultPayload,
  getJobTalentPoolQueryAndFormValues,
  getUpdateJobTalentPoolQuery,
  IJobTalentPoolFormType,
  JobTalentFilterKey,
} from '../../utils/JobTalentPoolHelper';

import JobTalentPoolAdvancedFilters from './JobTalentPoolAdvancedFilters';
import JobTalentPoolCandidateListing from './JobTalentPoolCandidateListing';
import JobTalentPoolHeader from './JobTalentPoolHeader';

const JobTalentPool = ({ job, booleanQuery }: { job: IJob; booleanQuery: string }) => {
  const [query, setQuery] = useState<IJobCandidateInteraction>(
    getJobTalentPoolDefaultPayload(job, booleanQuery)
  );

  const methods = useForm<IJobTalentPoolFormType>({
    defaultValues: {
      ...getFormDefaultFilters(query),
    },
  });

  const { handleSubmit, reset, getValues } = methods;
  const [loader, setLoader] = useState<boolean>(false);
  const candidates = useTalentPoolSearch(query, true, setLoader);

  const [filterTags, setFilterTags] = useState<FiltersTagProp[]>(getActiveFilters(getValues()));

  const onFormSubmit = (data: FieldValues) => {
    const updatedQuery = { ...getUpdateJobTalentPoolQuery(data as IJobTalentPoolFormType, query) };
    setQuery(updatedQuery);
    setFilterTags(getActiveFilters(data as IJobTalentPoolFormType));
  };

  const handleReset = () => {
    const updatedQuery = getJobTalentPoolDefaultPayload(job, booleanQuery);
    const updatedFormValues = getFormDefaultFilters(updatedQuery);
    setQuery(updatedQuery);
    reset(updatedFormValues);
    setFilterTags(getActiveFilters(updatedFormValues));
  };

  const handleUpdateJobTalentPoolQuery = (updateQuery: IJobCandidateInteraction) => {
    setQuery(updateQuery);
    setFilterTags(getActiveFilters(getValues()));
  };

  const onPageChange = (_, value: number) => {
    const updatedQuery = { ...query, page: value };
    setQuery(updatedQuery);
  };

  const onFilterTagRemoval = (id: string) => {
    const [key, index] = id.split('#');

    const { updatedFormValues, updatedQuery } = getJobTalentPoolQueryAndFormValues(
      key as JobTalentFilterKey,
      query,
      getValues(),
      Number(index)
    );
    reset(updatedFormValues);
    setFilterTags(getActiveFilters(updatedFormValues));
    setQuery(updatedQuery);
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)} onReset={handleReset}>
        <Grid display="flex" flexDirection="column" rowGap={2}>
          <JobTalentPoolHeader
            filterTags={filterTags}
            onFilterTagRemoval={onFilterTagRemoval}
            jobTalentPoolQuery={query}
            updateJobTalentPoolQuery={handleUpdateJobTalentPoolQuery}
          />
          <Grid container spacing={2}>
            <Grid item xs={9}>
              <Box>
                {loader ? (
                  <WsListingLoader />
                ) : (
                  <JobTalentPoolCandidateListing
                    job={job}
                    data={candidates}
                    pagination={{
                      size: query.size ?? 20,
                      pageNumber: query.page,
                    }}
                    onPageChange={onPageChange}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={3}>
              <JobTalentPoolAdvancedFilters />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default JobTalentPool;
